import React from 'react';
import Label from '../atoms/Label';
import ReactSelect from '../atoms/ReactSelect';
import { cn } from '../utils/cn';

/**
 * FormReactSelect Component - Molecule
 * Combines Label and ReactSelect atoms
 * 
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.name - Select name
 * @param {Array} props.options - Options array [{value, label}]
 * @param {Object|Array} props.value - Selected value(s)
 * @param {Function} props.onChange - Change handler
 * @param {string} props.error - Error message
 * @param {string} props.helperText - Helper text
 * @param {boolean} props.required - Required field
 */
const FormReactSelect = React.forwardRef(
  (
    {
      label,
      name,
      options = [],
      value,
      onChange,
      error,
      helperText,
      required = false,
      isMulti = false,
      className = '',
      labelClassName = '',
      ...selectProps
    },
    ref
  ) => {
    const handleChange = (selectedOption) => {
      if (onChange) {
        // Create synthetic event for compatibility
        const event = {
          target: {
            name,
            value: isMulti
              ? selectedOption?.map((opt) => opt.value) || []
              : selectedOption?.value || '',
          },
        };
        onChange(event);
      }
    };

    // Convert value to react-select format
    const selectValue = isMulti
      ? Array.isArray(value)
        ? options.filter((opt) => value.includes(opt.value))
        : []
      : options.find((opt) => opt.value === value) || null;

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label
            htmlFor={name}
            required={required}
            className={labelClassName}
          >
            {label}
          </Label>
        )}
        <ReactSelect
          ref={ref}
          id={name}
          name={name}
          options={options}
          value={selectValue}
          onChange={handleChange}
          isMulti={isMulti}
          variant={error ? 'error' : 'default'}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={
            error || helperText
              ? `${name}-${error ? 'error' : 'helper'}`
              : undefined
          }
          {...selectProps}
        />
        {error && (
          <p
            id={`${name}-error`}
            className="text-sm text-error-600 dark:text-error-400"
            role="alert"
          >
            {error}
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${name}-helper`}
            className="text-sm text-primary-500 dark:text-primary-400"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

FormReactSelect.displayName = 'FormReactSelect';

export default FormReactSelect;
